import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { UserDetail, UserRole, UserStatus } from '../../types/admin';
import clsx from 'clsx';

interface UserFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (user: Partial<UserDetail>) => Promise<void>;
  onUserCreated?: (tempPassword: string) => void;
  user?: UserDetail;
  mode: 'create' | 'edit';
}

interface FormData {
  email: string;
  full_name: string;
  role: UserRole;
  status: UserStatus;
  password?: string;
}

export const UserFormDialog: React.FC<UserFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onUserCreated,
  user,
  mode,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    email: '',
    full_name: '',
    role: 'user',
    status: 'active',
    password: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Generate a random temporary password
  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Reset form when dialog opens or changes
  const resetForm = useCallback(() => {
    const newPassword = mode === 'create' ? generatePassword() : '';
    setFormData({
      email: user?.email || '',
      full_name: user?.full_name || '',
      role: user?.role || 'user',
      status: user?.status || 'active',
      password: newPassword,
    });
    setErrors({});
    setLoading(false);
  }, [user, mode]);

  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      await onSave(formData);
      if (mode === 'create' && formData.password && onUserCreated) {
        onUserCreated(formData.password);
      }
      onClose();
    } catch (_error) {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof FormData, value: string | UserRole | UserStatus) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? t('admin.dialog.addUser') : t('admin.dialog.editUser')}
      maxWidth='md'
      showCloseButton={!loading}
      dataTestId={mode === 'create' ? 'create-user-dialog' : 'edit-user-dialog'}
      footer={
        <div className='flex justify-end space-x-3'>
          <button
            type='button'
            data-testid={mode === 'create' ? 'cancel-create-button' : 'cancel-edit-button'}
            onClick={onClose}
            disabled={loading}
            className='px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {t('common.cancel')}
          </button>
          <button
            type='button'
            data-testid={mode === 'create' ? 'create-user-submit' : 'edit-user-submit'}
            onClick={handleSubmit}
            disabled={loading}
            className={clsx(
              'px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
              'bg-blue-600 text-white hover:bg-blue-700'
            )}
          >
            {loading ? (
              <span className='flex items-center'>
                <svg
                  className='animate-spin -ml-1 mr-2 h-4 w-4'
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                >
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  ></circle>
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                  ></path>
                </svg>
                {t('admin.dialog.saving')}
              </span>
            ) : (
              t('common.save')
            )}
          </button>
        </div>
      }
    >
      <form onSubmit={e => e.preventDefault()} className='space-y-4'>
        {/* Full Name */}
        <div>
          <label htmlFor='full_name' className='block text-sm font-medium text-gray-700 mb-1'>
            {t('admin.dialog.fullNameLabel')} *
          </label>
          <input
            id='full_name'
            data-testid='user-name-input'
            type='text'
            value={formData.full_name}
            onChange={e => handleChange('full_name', e.target.value)}
            disabled={loading}
            placeholder={t('admin.dialog.fullNamePlaceholder')}
            className={clsx(
              'w-full px-3 py-2 border rounded-lg',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              'disabled:bg-gray-100 disabled:cursor-not-allowed',
              errors.full_name ? 'border-red-500' : 'border-gray-300'
            )}
          />
          {errors.full_name && (
            <p data-testid='name-error' className='mt-1 text-sm text-red-600'>
              {errors.full_name}
            </p>
          )}
        </div>

        {/* Email */}
        <div>
          <label htmlFor='email' className='block text-sm font-medium text-gray-700 mb-1'>
            {t('admin.dialog.emailLabel')} *
          </label>
          <input
            id='email'
            data-testid='user-email-input'
            type='email'
            value={formData.email}
            onChange={e => handleChange('email', e.target.value)}
            disabled={loading || mode === 'edit'}
            placeholder={t('admin.dialog.emailPlaceholder')}
            className={clsx(
              'w-full px-3 py-2 border rounded-lg',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              'disabled:bg-gray-100 disabled:cursor-not-allowed',
              errors.email ? 'border-red-500' : 'border-gray-300'
            )}
          />
          {errors.email && (
            <p data-testid='email-error' className='mt-1 text-sm text-red-600'>
              {errors.email}
            </p>
          )}
          {mode === 'edit' && (
            <p className='mt-1 text-xs text-gray-500'>{t('admin.dialog.emailNotEditable')}</p>
          )}
        </div>

        {/* Temporary Password - only show in create mode */}
        {mode === 'create' && (
          <div>
            <label htmlFor='password' className='block text-sm font-medium text-gray-700 mb-1'>
              {t('admin.dialog.tempPasswordLabel')}
            </label>
            <div className='flex space-x-2'>
              <div className='flex-1 relative'>
                <input
                  id='password'
                  data-testid='user-password-input'
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  readOnly
                  className={clsx(
                    'w-full px-3 py-2 pr-10 border rounded-lg bg-gray-50 font-mono text-sm',
                    'border-gray-300 text-gray-700'
                  )}
                />
                <button
                  type='button'
                  onClick={() => setShowPassword(!showPassword)}
                  className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700'
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <button
                type='button'
                onClick={() => {
                  const newPassword = generatePassword();
                  setFormData(prev => ({ ...prev, password: newPassword }));
                  setShowPassword(false);
                }}
                disabled={loading}
                className='px-3 py-2 border border-ui-primary text-ui-primary rounded-lg hover:bg-ui-primary hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap'
              >
                {t('admin.dialog.regenerate')}
              </button>
            </div>
            {errors.password && (
              <p data-testid='password-error' className='mt-1 text-sm text-red-600'>
                {errors.password}
              </p>
            )}
            <p className='mt-1 text-xs text-gray-500'>{t('admin.dialog.tempPasswordHint')}</p>
          </div>
        )}

        {/* Role */}
        <div>
          <label htmlFor='role' className='block text-sm font-medium text-gray-700 mb-1'>
            {t('admin.dialog.roleLabel')}
          </label>
          <select
            id='role'
            data-testid='user-role-select'
            value={formData.role}
            onChange={e => handleChange('role', e.target.value as UserRole)}
            disabled={loading || user?.id === 1}
            className={clsx(
              'w-full px-3 py-2 border rounded-lg',
              'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
              'disabled:bg-gray-100 disabled:cursor-not-allowed',
              'border-gray-300'
            )}
          >
            <option value='viewer' data-testid='role-viewer'>
              {t('admin.roles.viewer')}
            </option>
            <option value='user' data-testid='role-user'>
              {t('admin.roles.user')}
            </option>
            <option value='admin' data-testid='role-admin'>
              {t('admin.roles.admin')}
            </option>
          </select>
          {user?.id === 1 && (
            <p className='mt-1 text-xs text-gray-500'>{t('admin.dialog.cannotChangeRootRole')}</p>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default UserFormDialog;
